import { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Collapse,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import StatusButton from 'src/components/StatusButton';
import ListActionBar from 'src/components/ListActionBar';
import { useTagOptions } from 'src/hooks/tag';
import {
  useInfiniteUnmatchedMatchedApps,
  useUnmatchedMatchedAppFiles,
  useUnmatchedMatchedAppGroupFileCount,
  useUpdateUnmatchedMatchedAppGroupStatuses,
} from 'src/hooks/wdac';
import {
  UNMATCHED_FILE_STATUS,
  getUnmatchedFileStatusLabel,
} from 'src/utils/enums/unmatchedFileStatus';
import {
  UNMATCHED_FILE_TABLE_LAST_SEEN_RANGE_OPTIONS,
  UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS,
} from './constants';
import { buildSharedParams } from './sharedQueryHelper';
import { appGroup } from './groupFilterUtils';
import {
  GroupStatusCell,
  SortableHeaderCell,
  TextCell,
  ViewAllFilesLink,
  rowKeyOf,
} from './UnmatchedGroupTableShared';
import UnmatchedFileStatusModal from './UnmatchedFileStatusModal';
import UnmatchedFileDetailsModal from './UnmatchedFileDetailsModal';
import CatalogTrustWarning from './CatalogTrustWarning';

const COLUMNS = [
  { key: 'trustStatus', label: 'Trust Status', width: 132 },
  { key: 'appName', label: 'Application', sortable: true, sortKey: 'appName' },
  { key: 'publisher', label: 'Publisher' },
  { key: 'fileCount', label: 'Files', align: 'right' },
];

const MatchedAppGroupRow = ({
  group,
  search,
  filesApiQuery,
  isExpanded,
  isGroupSelected,
  queriesEnabled,
  onToggleExpand,
  onToggleGroup,
  onOpenGroupStatus,
  onOpenFileDetails,
  onViewFilesForGroup,
}) => {
  const rowKey = rowKeyOf(group);

  const groupFilesQuery = useMemo(() => {
    const q = { ...filesApiQuery };
    if (group.groupKey != null) {
      q.globalApplicationVersionId = group.groupKey;
    }
    return q;
  }, [filesApiQuery, group.groupKey]);

  const { data, isLoading, isFetching } = useUnmatchedMatchedAppFiles({
    apiQuery: groupFilesQuery,
    page: 1,
    limit: 10,
    enabled: queriesEnabled && isExpanded,
  });

  const files = useMemo(() => data?.results || [], [data]);

  return (
    <>
      <TableRow
        hover
        onClick={() => onToggleExpand(rowKey)}
        sx={{ cursor: 'pointer', '& td': { py: 0.6 } }}>
        <GroupStatusCell
          selected={isGroupSelected}
          status={group.groupTrustStatus}
          onToggle={() => onToggleGroup(group)}
          onOpenStatus={() => onOpenGroupStatus(group)}
        />
        <TableCell sx={{ maxWidth: 0 }}>
          <TextCell text={group.appName} searchValue={search} />
        </TableCell>
        <TableCell sx={{ maxWidth: 0 }}>
          <TextCell text={group.publisher} searchValue={search} muted />
        </TableCell>
        <TableCell align="right" sx={{ width: 90 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
            <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {(group.fileCount ?? 0).toLocaleString()}
            </Typography>
            <Iconify
              icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              width={16}
              height={16}
              color="#9CA3AF"
            />
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={COLUMNS.length} sx={{ p: 0, border: 0 }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ backgroundColor: '#3A3A3A', px: 2, py: 1 }}>
              {isLoading || (isFetching && files.length === 0) ? (
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  Loading files…
                </Typography>
              ) : files.length === 0 ? (
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  No files found for this application.
                </Typography>
              ) : (
                <Stack gap={0.25}>
                  {files.map(file => (
                    <Stack
                      key={file.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={() => onOpenFileDetails(file)}
                      sx={{
                        py: 0.25,
                        cursor: 'pointer',
                        borderRadius: '4px',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                      }}>
                      <StatusButton
                        status={file.trustStatus}
                        outlined
                        size="small"
                        disableRipple
                        sx={{
                          fontSize: '10px',
                          padding: '1px 5px',
                          minWidth: 0,
                          cursor: 'pointer',
                        }}>
                        {getUnmatchedFileStatusLabel(file.trustStatus)}
                      </StatusButton>
                      <CatalogTrustWarning
                        trustStatus={file.trustStatus}
                        trustedAsOfVersion={file.trustedAsOfVersion}
                        noTooltip
                        width={13}
                        height={13}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          color: '#F3F4F6',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: '0 1 auto',
                          maxWidth: '40%',
                        }}>
                        {file.fileName || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          color: '#9CA3AF',
                          fontFamily: 'monospace',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}>
                        {file.sha256}
                      </Typography>
                    </Stack>
                  ))}
                  <ViewAllFilesLink
                    onClick={() =>
                      onViewFilesForGroup(appGroup(group.globalApplicationVersionId, group.appName))
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Stack>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const UnmatchedMatchedAppsTable = ({
  filters,
  onFiltersChange,
  search = '',
  onSearchChange,
  queriesEnabled = true,
  onViewFilesForGroup,
  onViewAllFiles,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: tagOptionsData } = useTagOptions({ search: '', enabled: true });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);

  const [sort, setSort] = useState({ column: '', direction: 'desc' });

  const trimmedSearch = search.trim();

  const apiQuery = useMemo(() => {
    const params = { ...buildSharedParams(filters) };
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }
    if (sort.column) {
      params.sortColumn = sort.column;
      params.sortDesc = sort.direction === 'desc';
    }
    return params;
  }, [filters, trimmedSearch, sort]);

  const appsQuery = useInfiniteUnmatchedMatchedApps({
    apiQuery,
    limit: 10,
    enabled: queriesEnabled,
  });

  const results = useMemo(
    () => appsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [appsQuery.data],
  );
  const totalCount = appsQuery.data?.pages?.[0]?.totalCount || 0;
  const ungroupedFileCount = appsQuery.data?.pages?.[0]?.ungroupedFileCount || 0;
  const isLoading = !queriesEnabled || appsQuery.isLoading;
  const isFetchingNextPage = appsQuery.isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (appsQuery.hasNextPage) {
      appsQuery.fetchNextPage();
    }
  }, [appsQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: appsQuery.hasNextPage,
    disabled: !queriesEnabled,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const onSortChange = setSort;
  const filesApiQuery = apiQuery;

  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedGroups, setSelectedGroups] = useState(() => new Set());
  const [statusModal, setStatusModal] = useState({ open: false, groups: [] });
  const [detailsModal, setDetailsModal] = useState({ open: false, row: null });

  const updateGroupStatuses = useUpdateUnmatchedMatchedAppGroupStatuses();

  useEffect(() => {
    setExpanded(new Set());
    setSelectedGroups(new Set());
  }, [filters, trimmedSearch, sort]);

  const toggleExpand = useCallback(rowKey => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  }, []);

  const toggleGroup = useCallback(group => {
    const rowKey = rowKeyOf(group);
    setSelectedGroups(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    column => {
      if (!column.sortable || !column.sortKey) {
        return;
      }
      if (sort.column === column.sortKey) {
        onSortChange({
          column: column.sortKey,
          direction: sort.direction === 'asc' ? 'desc' : 'asc',
        });
      } else {
        onSortChange({ column: column.sortKey, direction: 'asc' });
      }
    },
    [onSortChange, sort],
  );

  const openFileDetails = useCallback(file => {
    setDetailsModal({ open: true, row: file });
  }, []);

  const openGroupStatus = useCallback(group => {
    setStatusModal({ open: true, groups: [group] });
  }, []);

  const openBulkStatus = useCallback(() => {
    const groups = results.filter(group => selectedGroups.has(rowKeyOf(group)));
    if (groups.length === 0) {
      return;
    }
    setStatusModal({ open: true, groups });
  }, [results, selectedGroups]);

  const saveStatusModal = useCallback(
    async (newStatus, note) => {
      const groups = statusModal.groups;
      if (groups.length === 0) {
        return;
      }
      try {
        await updateGroupStatuses.mutateAsync({
          query: {
            globalApplicationVersionIds: groups.map(group => group.globalApplicationVersionId),
            ...buildSharedParams(filters),
          },
          payload: {
            status: newStatus,
            notes: note,
          },
        });
        enqueueSnackbar(groups.length > 1 ? 'Statuses updated' : 'Status updated', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        setStatusModal({ open: false, groups: [] });
        setSelectedGroups(new Set());
      } catch (error) {
        enqueueSnackbar(`Could not update status: ${error?.message || error}`, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [statusModal.groups, updateGroupStatuses, filters, enqueueSnackbar],
  );

  const filterOptions = useMemo(
    () => [
      {
        key: 'trustSignal',
        type: 'select',
        label: 'Signal',
        options: UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS,
        disabled: currentFilters => currentFilters.trustStatus !== UNMATCHED_FILE_STATUS.Unknown,
      },
      {
        key: 'tagId',
        type: 'select',
        label: 'Device Tag',
        options: [
          { value: '', label: 'All Tags' },
          ...tagOptions.map(tag => ({ value: String(tag.id), label: tag.name })),
        ],
      },
      {
        key: 'lastSeenRange',
        type: 'select',
        label: 'Last Seen',
        options: UNMATCHED_FILE_TABLE_LAST_SEEN_RANGE_OPTIONS,
      },
      {
        key: 'trustStatus',
        type: 'select',
        label: 'Trust Status',
        options: [
          { value: '', label: 'All statuses' },
          ...Object.values(UNMATCHED_FILE_STATUS).map(value => ({
            value,
            label: getUnmatchedFileStatusLabel(value),
          })),
        ],
      },
    ],
    [tagOptions],
  );

  const onFilterChange = useCallback(
    nextFilters => {
      const tagValue =
        nextFilters.tagId === null || nextFilters.tagId === undefined ? '' : nextFilters.tagId;
      onFiltersChange({
        ...filters,
        tagId: tagValue,
        trustStatus: nextFilters.trustStatus || '',
        trustSignal:
          nextFilters.trustStatus === UNMATCHED_FILE_STATUS.Unknown
            ? nextFilters.trustSignal || ''
            : '',
        lastSeenRange: nextFilters.lastSeenRange || '',
      });
    },
    [filters, onFiltersChange],
  );

  const selectedCount = selectedGroups.size;
  const modalGroups = statusModal.groups;
  const modalSummary = modalGroups.length === 1 ? modalGroups[0].appName || '--' : undefined;
  const fileCountQuery = useUnmatchedMatchedAppGroupFileCount({
    payload: {
      globalApplicationVersionIds: modalGroups.map(group => group.globalApplicationVersionId),
      ...buildSharedParams(filters),
    },
    enabled: statusModal.open && modalGroups.length > 0,
  });

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={onSearchChange}
        defaultSearch={search}
        searchPlaceholder="Application or publisher name"
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={4}
        filterButtonCount={totalCount}
        actionArea={
          selectedCount > 0 ? (
            <Button
              variant="outlined"
              onClick={openBulkStatus}
              sx={{
                backgroundColor: '#3F3F3F',
                borderColor: '#2E3236',
                color: '#ffffff',
                textTransform: 'none',
                padding: '4px 14px',
                minHeight: 32,
              }}>
              Change Status (files in {selectedCount} app{selectedCount === 1 ? '' : 's'})
            </Button>
          ) : null
        }
      />

      <TableContainer roundedTop styles={{ maxHeight: 480 }}>
        <Table
          stickyHeader
          size="small"
          sx={{ width: '100%', '& .MuiTableCell-root': { fontSize: '0.72rem' } }}>
          <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
            <TableRow>
              {COLUMNS.map(column => (
                <SortableHeaderCell
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSort={handleSort}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {isLoading ? (
              <LoadingRows columnCount={COLUMNS.length} rowCount={10} />
            ) : (
              <>
                {results?.map((group, index) => {
                  const rowKey = rowKeyOf(group);
                  return (
                    <MatchedAppGroupRow
                      key={`${rowKey}-${index}`}
                      group={group}
                      search={trimmedSearch}
                      filesApiQuery={filesApiQuery}
                      isExpanded={expanded.has(rowKey)}
                      isGroupSelected={selectedGroups.has(rowKey)}
                      queriesEnabled={queriesEnabled}
                      onToggleExpand={toggleExpand}
                      onToggleGroup={toggleGroup}
                      onOpenGroupStatus={openGroupStatus}
                      onOpenFileDetails={openFileDetails}
                      onViewFilesForGroup={onViewFilesForGroup}
                    />
                  );
                })}
                {!isLoading && !isFetchingNextPage && !results?.length && (
                  <NoDataCell colSpan={COLUMNS.length}>No matched apps found.</NoDataCell>
                )}
                {!isLoading && (isFetchingNextPage || appsQuery.hasNextPage) ? (
                  <TableRow ref={sentryRef}>
                    <TableCell colSpan={COLUMNS.length} sx={{ border: 0, py: 1 }}>
                      <Stack spacing={0.75}>
                        {[0, 1, 2].map(i => (
                          <Skeleton
                            key={i}
                            variant="rounded"
                            height={14}
                            sx={{ bgcolor: '#454545' }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading && !isFetchingNextPage && ungroupedFileCount > 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} sx={{ borderBottom: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography
                          variant="caption"
                          sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>
                          {ungroupedFileCount.toLocaleString()} more file
                          {ungroupedFileCount === 1 ? " isn't" : "s aren't"} in apps.
                        </Typography>
                        <ViewAllFilesLink onClick={onViewAllFiles} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UnmatchedFileStatusModal
        open={statusModal.open}
        onClose={() => setStatusModal({ open: false, groups: [] })}
        noun="app"
        count={modalGroups.length}
        fileCount={fileCountQuery.data?.fileCount}
        fileCountLoading={fileCountQuery.isFetching}
        summary={modalSummary}
        initialStatus={modalGroups.length === 1 ? modalGroups[0].groupTrustStatus : undefined}
        onSave={saveStatusModal}
        loading={updateGroupStatuses.isLoading}
      />

      <UnmatchedFileDetailsModal
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, row: null })}
        statusId={detailsModal.row?.id}
        fallbackRow={detailsModal.row}
        onViewFilesForGroup={onViewFilesForGroup}
      />
    </Box>
  );
};

export default UnmatchedMatchedAppsTable;
